import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { HierarchyNode, OrganisationsService } from '../../api';
import type { ScopeContext } from './scope-format';

/** A hierarchy entity an insight can be scoped to, with the organisation that owns it. */
export interface HierarchyScopeNode {
  name: string;
  typeLabel: string;
  organisationName: string | null;
  /** The Organisation above the entity (or the entity itself), or null when there is none or it lacks an id or name. */
  owningOrganisation: { id: string; name: string } | null;
  /** The organisations, sites and facilities the entity covers, itself included. */
  scope?: ScopeContext;
}

export type HierarchyScopeIndex = ReadonlyMap<string, HierarchyScopeNode>;

/**
 * Builds the key an insight's scope entity is looked up by. The API reports entity types in
 * snake-cased lower case ("facility") while the hierarchy labels them for display ("Facility"),
 * so both sides are folded to lower case.
 *
 * @param entityType The scope entity type.
 * @param entityId The source-system id of the scope entity.
 * @returns The lookup key.
 */
export function scopeKey(entityType: string, entityId: string): string {
  return `${entityType.toLowerCase()}:${entityId}`;
}

/**
 * Indexes the flattened hierarchy by scope key, resolving each entity's owning organisation.
 *
 * @param nodes The hierarchy as the API returned it.
 * @returns A lookup of scope key to entity, including its owning organisation name.
 */
function buildScopeIndex(nodes: HierarchyNode[]): Map<string, HierarchyScopeNode> {
  const byNodeId = new Map<number, HierarchyNode>();
  for (const node of nodes) {
    byNodeId.set(node.nodeId, node);
  }

  /**
   * Walks up from a node through its parents to the first ancestor (or the node itself) whose
   * type label is one of the given labels.
   *
   * @param node The node to start from.
   * @param stopAt The lower-case type labels to stop at.
   * @returns The first matching node, or undefined when the walk reaches the root without a match.
   */
  const walkUpTo = (node: HierarchyNode, stopAt: readonly string[]): HierarchyNode | undefined => {
    let current: HierarchyNode | undefined = node;
    while (current) {
      if (stopAt.includes((current.typeLabel ?? '').toLowerCase())) return current;
      current = current.parentNodeId == null ? undefined : byNodeId.get(current.parentNodeId);
    }
    return undefined;
  };

  // A hierarchy that files the entity directly under a Company has no organisation level to
  // report, so the Company stands in as the owner name.
  const ownerNameOf = (node: HierarchyNode): string | null =>
    walkUpTo(node, ['organisation', 'company'])?.name ?? null;

  /**
   * Finds the Organisation that owns a node: the node itself or its nearest Organisation ancestor.
   *
   * @param node The node to resolve.
   * @returns The organisation's id and name, or null when there is none or it lacks an id or name.
   */
  const owningOrganisationOf = (node: HierarchyNode): { id: string; name: string } | null => {
    const organisation = walkUpTo(node, ['organisation']);
    return organisation?.id && organisation.name ? { id: organisation.id, name: organisation.name } : null;
  };

  const childrenOf = new Map<number, HierarchyNode[]>();
  for (const node of nodes) {
    if (node.parentNodeId == null) continue;
    const siblings = childrenOf.get(node.parentNodeId) ?? [];
    siblings.push(node);
    childrenOf.set(node.parentNodeId, siblings);
  }

  const scopeOf = (root: HierarchyNode): ScopeContext => {
    const scope: ScopeContext = { onRoad: { organisations: [], sites: [] }, onSite: { facilities: [] } };
    const visited = new Set<number>();
    const stack = [root];
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current.nodeId)) continue;
      visited.add(current.nodeId);
      const name = current.name ?? '';
      switch ((current.typeLabel ?? '').toLowerCase()) {
        case 'organisation':
          scope.onRoad.organisations.push(name);
          break;
        case 'site':
          scope.onRoad.sites.push(name);
          break;
        case 'facility':
          scope.onSite.facilities.push(name);
          break;
      }
      stack.push(...(childrenOf.get(current.nodeId) ?? []));
    }
    return scope;
  };

  const index = new Map<string, HierarchyScopeNode>();
  for (const node of nodes) {
    if (!node.id || !node.typeLabel) continue;
    const typeLabel = node.typeLabel.toLowerCase();
    index.set(scopeKey(node.typeLabel, node.id), {
      name: node.name ?? '',
      typeLabel: node.typeLabel,
      organisationName: ownerNameOf(node),
      owningOrganisation: owningOrganisationOf(node),
      ...(typeLabel === 'organisation' || typeLabel === 'company' ? { scope: scopeOf(node) } : {}),
    });
  }
  return index;
}

/**
 * Holds the organisation hierarchy so that any feature can name the entity an insight belongs to.
 * The filters component is what retrieves it; everything else reads the resulting index.
 */
@Injectable({ providedIn: 'root' })
export class OrganisationHierarchyService {
  private readonly organisationsService = inject(OrganisationsService);
  private readonly index = signal<HierarchyScopeIndex>(new Map<string, HierarchyScopeNode>());

  /** Scope key to entity lookup, empty until the hierarchy has been retrieved. */
  readonly scopeIndex = computed<HierarchyScopeIndex>(() => this.index());

  /**
   * Retrieves the hierarchy and indexes it for scope lookups.
   *
   * @returns The hierarchy as the API returned it.
   */
  load(): Observable<HierarchyNode[]> {
    return this.organisationsService
      .getHierarchyOrganisationsHierarchyGet()
      .pipe(tap(nodes => this.index.set(buildScopeIndex(nodes))));
  }
}
